import React, { useEffect, useState } from 'react';
import { getMockConfig, getMockConfigDefaults, saveMockConfig } from '../ai-interview/utils/aiInterviewApi';

/**
 * Super-Admin: the ONE global Job Seeker Mock Interview configuration (shared by every job seeker).
 * Mock interviews are AI-only and assess the ROLE rather than hitting a fixed question count. The admin
 * picks the FOCUS AREAS (which topics to cover), the DURATION, and the FOLLOW-UP depth — the AI then
 * sizes the interview to the time and spreads questions across the chosen areas, deciding the count and
 * follow-ups itself. (A legacy "fixed count" mode is still available as a fallback.)
 */
const Section = ({ title, desc, children }) => (
  <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-gray-900">
    <div className="mb-4">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h2>
      {desc && <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">{desc}</p>}
    </div>
    {children}
  </div>
);

const fieldCls = 'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500/40 dark:border-gray-600 dark:bg-gray-900 dark:text-white';

const NumberField = ({ label, hint, value, onChange, min = 1 }) => (
  <label className="block text-sm">
    <span className="mb-1 block font-medium text-gray-700 dark:text-gray-200">{label}</span>
    <input type="number" min={min} value={value ?? ''} onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))} className={fieldCls} />
    {hint && <span className="mt-0.5 block text-xs text-gray-400">{hint}</span>}
  </label>
);

const SelectField = ({ label, hint, value, onChange, options }) => (
  <label className="block text-sm">
    <span className="mb-1 block font-medium text-gray-700 dark:text-gray-200">{label}</span>
    <select value={value} onChange={(e) => onChange(e.target.value)} className={fieldCls}>
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
    {hint && <span className="mt-0.5 block text-xs text-gray-400">{hint}</span>}
  </label>
);

// A focus-area card the admin toggles on/off.
const FocusCard = ({ name, label, description, on, onToggle }) => (
  <button type="button" onClick={onToggle}
    className={`flex items-start justify-between gap-3 rounded-xl border p-3 text-left transition ${on
      ? 'border-primary-300 bg-primary-50/50 dark:border-primary-800 dark:bg-primary-900/20'
      : 'border-gray-200 bg-white hover:border-gray-300 dark:border-gray-700 dark:bg-gray-900 dark:hover:border-gray-600'}`}>
    <span className="min-w-0">
      <span className="block text-sm font-semibold text-gray-800 dark:text-gray-100">{label || name}</span>
      {description && <span className="mt-0.5 block text-xs text-gray-400 dark:text-gray-500">{description}</span>}
    </span>
    <span className={`mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full text-[11px] font-bold ${on
      ? 'bg-primary-600 text-white' : 'border border-gray-300 text-transparent dark:border-gray-600'}`}>✓</span>
  </button>
);

const FOLLOWUP_OPTIONS = [
  ['strict', 'Strict — ask & move on'],
  ['balanced', 'Balanced — 1–2 follow-ups'],
  ['deep_dive', 'Deep dive — probe each answer'],
];

const MockInterviewConfigPage = () => {
  const [registry, setRegistry] = useState([]);
  const [form, setForm] = useState(null);      // { question_count_mode, total_questions, duration_minutes, followup_mode }
  const [focus, setFocus] = useState(new Set()); // enabled focus-area category names
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const d = await getMockConfigDefaults();
        const { configuration } = await getMockConfig();
        if (!alive) return;
        const reg = d.categories || [];
        const base = configuration || {};
        setRegistry(reg);
        setForm({
          question_count_mode: base.question_count_mode || d.defaults.question_count_mode || 'unrestricted',
          total_questions: base.total_questions ?? d.defaults.total_questions,
          duration_minutes: base.duration_minutes ?? d.defaults.duration_minutes,
          followup_mode: base.followup_mode || d.defaults.followup_mode || 'balanced',
        });
        // Saved focus areas → use them; first-time setup → default to every registry area enabled.
        const saved = configuration?.focus_categories
          || (configuration?.categories || []).filter((c) => c.enabled !== false).map((c) => c.category_name);
        setFocus(new Set(saved && saved.length ? saved : reg.map((c) => c.name)));
      } catch (e) {
        setMsg({ type: 'error', text: e.message || 'Could not load the mock configuration.' });
      }
    })();
    return () => { alive = false; };
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleFocus = (name) => setFocus((s) => {
    const next = new Set(s);
    if (next.has(name)) next.delete(name); else next.add(name);
    return next;
  });
  const isUnrestricted = form && form.question_count_mode !== 'fixed';

  const save = async () => {
    setBusy(true);
    setMsg(null);
    try {
      await saveMockConfig({
        question_count_mode: form.question_count_mode,
        total_questions: Number(form.total_questions) || 0,
        duration_minutes: Number(form.duration_minutes) || 0,
        followup_mode: form.followup_mode,
        focus_categories: registry.map((c) => c.name).filter((n) => focus.has(n)),
      });
      setMsg({ type: 'success', text: 'Saved. New mock interviews will use these settings.' });
    } catch (e) {
      setMsg({ type: 'error', text: e.message || 'Could not save.' });
    } finally { setBusy(false); }
  };

  if (!form) return <div className="p-8 text-gray-500 dark:text-gray-400">Loading mock interview configuration…</div>;

  return (
    <div className="px-6 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Mock Interview Configuration</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          One global configuration for every Job Seeker mock interview (AI-only, voice + chat). Pick the focus areas and the length — the AI assesses the role across those areas and decides how many questions to ask. Changes apply to mock interviews started after saving.
        </p>
      </div>

      <div className="space-y-5">
        <Section title="Length & depth" desc="How long the interview runs and how the AI sizes it.">
          <div className="grid gap-3 sm:grid-cols-2">
            <SelectField label="Question count" value={form.question_count_mode}
              onChange={(v) => set('question_count_mode', v)}
              options={[['unrestricted', 'Cover the role (AI decides count)'], ['fixed', 'Fixed count']]}
              hint={isUnrestricted ? 'The AI asks as many as the role needs, bounded by the duration.' : ''} />
            <NumberField label="Duration (minutes)" value={form.duration_minutes} onChange={(v) => set('duration_minutes', v)} />
            {!isUnrestricted && (
              <NumberField label="Total questions" hint="Used only in fixed-count mode" value={form.total_questions} onChange={(v) => set('total_questions', v)} />
            )}
            <SelectField label="Follow-up depth" value={form.followup_mode}
              onChange={(v) => set('followup_mode', v)} options={FOLLOWUP_OPTIONS}
              hint="How much the AI probes each answer." />
          </div>
        </Section>

        <Section title="Focus areas" desc="The topics the AI assesses. It spreads questions across the selected areas (limited to what the candidate’s resume / job description supports) and covers the role within the duration.">
          {registry.length === 0 ? (
            <p className="text-sm text-gray-400">No interview categories available.</p>
          ) : (
            <>
              <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
                {registry.map((c) => (
                  <FocusCard key={c.name} name={c.name} label={c.label} description={c.description}
                    on={focus.has(c.name)} onToggle={() => toggleFocus(c.name)} />
                ))}
              </div>
              <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
                {focus.size} of {registry.length} areas selected{focus.size === 0 ? ' — the AI will cover all relevant areas by default.' : '.'}
              </p>
            </>
          )}
        </Section>
      </div>

      <div className="mt-6 flex items-center gap-3">
        <button type="button" onClick={save} disabled={busy}
          className="rounded-lg bg-primary-600 px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-700 disabled:opacity-60">
          {busy ? 'Saving…' : 'Save configuration'}
        </button>
        {msg && (
          <span className={`text-sm ${msg.type === 'success' ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>{msg.text}</span>
        )}
      </div>
    </div>
  );
};

export default MockInterviewConfigPage;
